import React, { useState, useRef, useEffect } from 'react';
import { HiChevronDown, HiXMark } from 'react-icons/hi2';
import SelectEntities from './SelectEntities';
import { StrategyMapListItem } from '../../services/strategyMap';
import { getEntityTypeDisplay } from '../../utils/entityDisplayUtils';
import './SelectEntityDropdown.css';

interface SelectEntityDropdownProps {
  selectedEntities: StrategyMapListItem[];
  onChange: (entities: StrategyMapListItem[]) => void;
  placeholder?: string;
  searchType?: "roadmap" | "project" | "epic" | "feature";
  selectOne?: boolean;
  defaultSelectedIds?: { [key: string]: string[] };
}

export function SelectEntityDropdown({
  selectedEntities,
  onChange,
  placeholder = "Select Strategy Maps or Work Units...",
  searchType,
  selectOne = false,
  defaultSelectedIds
}: SelectEntityDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingDefaultIds, setIsLoadingDefaultIds] = useState(false);
  const [defaultSelectedIdsState, setDefaultSelectedIdsState] = useState<any>(defaultSelectedIds);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [refresh, setRefresh] = useState(0);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setDefaultSelectedIdsState(defaultSelectedIds);
  }, []);

  const handleRemove = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    onChange(selectedEntities.filter(item => item.id !== id));
    setDefaultSelectedIdsState(null);
    setRefresh(r => r + 1)
  };

  return (
    <div
      className={`select-entity-dropdown ${isOpen ? "is-open" : ""}`}
      ref={dropdownRef}
    >
      <div
        className="select-entity-dropdown__trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="select-entity-dropdown__selected-container">
          {selectedEntities.length > 0 ? (
            <div className="select-entity-dropdown__tags">
              {selectedEntities.map((item) => {
                const entityInfo = getEntityTypeDisplay(
                  item.entity_type,
                  item.type,
                );
                const Icon = entityInfo.icon;

                return (
                  <div key={item.id} className="select-entity-dropdown__tag">
                    {/* @ts-ignore */}
                    <Icon
                      className={`select-entity-dropdown__tag-icon ${entityInfo.iconClass}`}
                      size={14}
                    />
                    <span className="select-entity-dropdown__tag-label">
                      {item.name}
                    </span>
                    <button
                      className="select-entity-dropdown__tag-remove"
                      onClick={(e) => handleRemove(e, item.id)}
                    >
                      <HiXMark size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <span className="select-entity-dropdown__placeholder">
              {placeholder}
            </span>
          )}
        </div>
        <div className="select-entity-dropdown__icons">
          {isLoadingDefaultIds && <div className="select-entity-dropdown__spinner" />}
          <HiChevronDown
            className={`select-entity-dropdown__chevron ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      {(
        <div className="select-entity-dropdown__menu">
          <SelectEntities
            visibility={isOpen}
            key={refresh}
            onChange={(entities) => {
              onChange(entities);
              if (selectOne) {
                // setIsOpen(false);
              }
            }}
            entityTypeLabel="Entity"
            searchFilterConfig={{
              visibility: false,
              value: searchType,
            }}
            selectType={selectOne ? "single" : "multiple"}
            defaultSelectedIds={defaultSelectedIdsState}
            defaultSelectedItemsData={selectedEntities}
            onDefaultIdsFetchStart={() => setIsLoadingDefaultIds(true)}
            onDefaultIdsFetchEnd={() => setIsLoadingDefaultIds(false)}
          />
        </div>
      )}
    </div>
  );
}